// Admin Contract Detail - Detalle de contrato con UI mobile-first
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { contractsService } from '@/services/contractsService';
import { mandateService } from '@/services/mandateService';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowTopRightOnSquareIcon, BanknotesIcon, CalendarIcon, ChartBarIcon, CheckIcon, ClockIcon, CreditCardIcon, CurrencyDollarIcon, DocumentDuplicateIcon, DocumentIcon, DocumentTextIcon, EnvelopeIcon, LinkIcon, PencilIcon, PencilSquareIcon, ScaleIcon, TrashIcon, UserIcon, UsersIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { Contract, ContractStatus } from '@/types/contracts';
import type { StripeBillingSummary } from '@/types/stripe';
import type { ContractUpdateRequest } from '@/types/contracts';
import {
  CONTRACT_STATUS_COLORS,
  GRADE_COLORS,
} from '@/types/contracts';
import { formatDate, formatDateOnly, formatCurrency } from '@/utils/formatters';
import {
  getContractTypeFromRaw,
  getExpectedAmountByPaymentType,
  getPaymentTypeLabel,
  getPaymentTypeMessages,
  getPaymentTypeTitle,
  isAmountMatchingExpected,
  parseContractAmountInput,
} from '@/utils/contractPaymentPricing';
import { usePageTitle } from '@/hooks/usePageTitle';
import { EditContractModal } from '@/components/contracts/EditContractModal';
import { ContractAnnexes } from '@/components/contracts/ContractAnnexes';
import { ContractMandateTab } from '@/components/contracts/ContractMandateTab';
import { ContractPaymentsTab } from '@/components/contracts/ContractPaymentsTab';
import { ContractSignaturesTab } from '@/components/contracts/ContractSignaturesTab';
import { StripeBillingSection } from '@/components/stripe/StripeBillingSection';
import { config } from '@/config/constants';
import { getMigroSignatureFromStorage } from '@/config/migroSignatureStorage';
import { toast } from 'sonner';
import { ActivityLogBox } from '@/components/CRM/ActivityLogBox';
import { ContractExpedienteLink } from '@/components/contracts/ContractExpedienteLink';

interface AdminContractDetailProps {
  contractsListPath?: string;
}

export function AdminContractDetail({ contractsListPath = '/crm/contracts' }: AdminContractDetailProps) {
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory({ fallbackPath: contractsListPath });
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<Contract | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExpireModal, setShowExpireModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [stripeSummary, setStripeSummary] = useState<StripeBillingSummary | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'cliente' | 'firmas' | 'pagos' | 'anexos' | 'stripe' | 'mandato' | 'actividad'>('general');
  const [updateForm, setUpdateForm] = useState({
    status: 'pending' as ContractStatus,
    amount: '',
    payment_type: 'one_time' as 'one_time' | 'subscription' | 'one_time_full',
    manual_payment_confirmed: false,
    manual_payment_method: '',
    manual_payment_note: '',
    subscription_id: '',
    subscription_status: '',
    first_payment_amount: '',
  });

  const loadContract = useCallback(async () => {
    if (!code) return;
    
    setLoading(true);
    try {
      const data = await contractsService.getContract(code);
      console.log('📋 Contrato cargado:', data);
      console.log('📋 service_name:', data.service_name);
      console.log('📋 service_description:', data.service_description);
      setContract(data);
    } catch (error) {
      console.error('Error cargando contrato:', error);
      setContract(null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  const loadStripeSummary = useCallback(async () => {
    if (!code || code === 'create') return;
    try {
      const data = await contractsService.getStripeBillingSummary(code);
      setStripeSummary(data);
    } catch {
      setStripeSummary(null);
    }
  }, [code]);

  useEffect(() => {
    if (code && code !== 'create') {
      loadContract();
    } else if (code === 'create') {
      // Redirigir a la lista de contratos ya que no hay página de creación todavía
      navigate(contractsListPath);
    } else {
      setLoading(false);
    }
  }, [code, navigate, loadContract, contractsListPath]);

  // Cargar resumen Stripe solo si el contrato ya tiene datos de pago Stripe.
  // Evita llamadas innecesarias (y errores 404/502) para contratos pendientes sin pago.
  useEffect(() => {
    if (contract?.payment_intent_id || contract?.subscription_id) {
      loadStripeSummary();
    }
  }, [contract?.payment_intent_id, contract?.subscription_id, loadStripeSummary]);

  const getPricingMessages = (paymentType: 'one_time' | 'subscription' | 'one_time_full') => {
    const contractType = getContractTypeFromRaw(contract?.contract_type);
    return getPaymentTypeMessages(paymentType, contract?.grade, contractType);
  };

  const generateLocalContract = async (isFinal: boolean = false) => {
    if (!contract) return;
    
    console.log('🔧 Generando contrato localmente...');
    
    // Importar dinámicamente para evitar problemas de bundle
    const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
    
    // Convertir Contract a HiringDetails para el generador de PDF
    // Mapear segundo holder (is_primary === false) a supplementary_holder para duo/family
    const secondHolder = contract.holders?.find(h => h.is_primary === false);
    const supplementaryHolder = secondHolder ? (() => {
      const dt = secondHolder.document_type as string | undefined;
      const docId = secondHolder.document_id || '';
      const isNie = dt === 'nie' || dt === 'nif' || (!dt && /^[XYZ]\d{7}[A-Z]$/i.test(docId));
      return {
        client_name: secondHolder.full_name || '',
        client_email: secondHolder.email || '',
        client_passport: (!isNie && docId) ? docId : undefined,
        client_nie: (isNie && docId) ? docId : undefined,
        client_nationality: secondHolder.nationality || undefined,
        client_address: secondHolder.address || undefined,
        client_city: secondHolder.city || undefined,
        client_province: secondHolder.province || undefined,
        client_postal_code: secondHolder.postal_code || undefined,
      };
    })() : undefined;

    const hiringDetails = {
      id: parseInt(contract.id) || 0,
      hiring_code: contract.hiring_code,
      client_name: contract.client_name,
      client_email: contract.client_email,
      service_name: contract.service_name,
      service_description: contract.service_description || '',
      amount: contract.amount,
      currency: contract.currency,
      status: (contract.status === 'pending' || contract.status === 'paid' || contract.status === 'completed')
        ? contract.status as 'pending' | 'paid' | 'completed'
        : 'pending' as const,
      kyc_status: contract.kyc_status,
      expires_at: contract.expires_at,
      short_url: contract.short_url,
      client_passport: contract.client_passport,
      client_nie: contract.client_nie,
      client_nationality: contract.client_nationality,
      client_address: contract.client_address,
      client_city: contract.client_city,
      client_province: contract.client_province,
      client_postal_code: contract.client_postal_code,
      contract_date: contract.contract_date,
      contract_accepted: contract.contract_accepted,
      contract_accepted_at: contract.contract_accepted_at,
      grade: contract.grade,
      payment_type: contract.payment_type,
      manual_payment_confirmed: contract.manual_payment_confirmed,
      manual_payment_note: contract.manual_payment_note,
      manual_payment_method: contract.manual_payment_method,
      payment_intent_id: contract.payment_intent_id,
      subscription_id: contract.subscription_id,
      subscription_status: contract.subscription_status,
      first_payment_amount: contract.first_payment_amount,
      contract_type: contract.contract_type,
      supplementary_holder: supplementaryHolder,
      holders: contract.holders,
      dependents: contract.dependents,
    };
    
    // Generar PDF localmente
    // Cargar anexos si existen
    let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
    try {
      const loadedAnnexes = await contractsService.getAnnexes(contract.hiring_code);
      if (loadedAnnexes.length > 0) {
        annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
      }
    } catch (annexError) {
      console.warn('No se pudieron cargar anexos:', annexError);
    }
    
    const contractBlob = generateContractPDF(hiringDetails, {
      paymentIntentId: contract.payment_intent_id || 'admin_generated',
      stripeTransactionId: contract.payment_intent_id || `admin_${Date.now()}`,
      paymentDate: contract.contract_accepted_at || new Date().toISOString(),
      paymentMethod: contract.manual_payment_method || 'Generado desde admin',
      paymentNote: contract.manual_payment_note,
      clientSignature: undefined, // No tenemos firma del cliente en el admin
      migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
    }, !isFinal, annexesToInclude, { language: (contract.language as 'es' | 'en' | 'fr') || 'es' }); // isDraft + idioma del contrato
    
    // Descargar el PDF generado
    const url = window.URL.createObjectURL(contractBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrato-${contract.hiring_code}${isFinal ? '-final' : ''}.pdf`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
    console.log('✅ Contrato generado y descargado localmente');
  };

  const handleDownload = async (isFinal: boolean = false) => {
    if (!contract) return;
    
    setDownloading(true);
    try {
      await contractsService.downloadContractFile(
        contract.hiring_code,
        `contrato-${contract.hiring_code}${isFinal ? '-final' : ''}.pdf`,
        isFinal
      );
      console.log('✅ Contrato descargado desde el backend');
    } catch (error: unknown) {
      console.warn('⚠️ No se pudo descargar desde el backend, generando localmente:', error);
      
      const err = error as { response?: { status?: number }; message?: unknown };
      const errMessage = typeof err?.message === 'string' ? err.message : '';

      // Verificar si es un error 404 u otro error
      const is404 =
        err?.response?.status === 404 ||
        errMessage.includes('404') ||
        errMessage.toLowerCase().includes('no encontrado') ||
        errMessage.includes('Recurso no encontrado');
      
      if (is404) {
        console.log('🔧 Contrato no disponible en backend (404), generando localmente...');
        try {
          await generateLocalContract(isFinal);
        } catch (localErr) {
          console.error('❌ Error generando contrato local:', localErr);
          toast.error('No se pudo generar el contrato. Contacta con soporte.');
        }
      } else {
        // Para otros errores, también intentar generar localmente
        try {
          await generateLocalContract(isFinal);
        } catch (localErr) {
          console.error('❌ Error generando contrato local:', localErr);
          toast.error('Error al descargar el contrato. Se intentó generar localmente pero falló.');
        }
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = () => {
    if (!contract) return;
    
    // Formato: migro.es/c/(codigo)
    const url = `${config.SHORT_URL_BASE}/c/${contract.hiring_code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenLink = () => {
    if (!contract) return;
    window.open(`/contratacion/${contract.hiring_code}`, '_blank');
  };

  /** Descarga del contrato definitivo: URL directa si existe; si no, CRM download (404 → toast) */
  const handleDownloadFinalSigned = async () => {
    if (!contract) return;
    if (contract.final_contract_url) {
      window.open(contract.final_contract_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setDownloading(true);
    try {
      const blob = await contractsService.downloadFinalContractCRM(contract.hiring_code);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number };
      if (e?.status === 404 || (typeof e?.message === 'string' && e.message.includes('no disponible'))) {
        toast.error('Contrato definitivo no disponible. Se genera después del pago confirmado.');
      } else {
        toast.error('No se pudo descargar el contrato definitivo.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenMandateLink = () => {
    if (!contract) return;
    window.open(`/mandato/${contract.hiring_code}`, '_blank');
  };

  /** Descarga mandato: URL directa si existe; si no, CRM por hiring_code (primer titular). Para varios mandatos se usa la pestaña Mandato. */
  const handleDownloadMandate = async () => {
    if (!contract) return;
    if (contract.mandate_pdf_url) {
      window.open(contract.mandate_pdf_url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const blob = await mandateService.downloadMandateCRM(contract.hiring_code, { holder_index: 0 });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number };
      if (e?.status === 404 || (typeof e?.message === 'string' && e.message.includes('no disponible'))) {
        toast.error('Mandato firmado no disponible.');
      } else {
        toast.error('No se pudo descargar el mandato.');
      }
    }
  };

  const handleResendWelcomeEmail = async () => {
    if (!contract || !code) return;
    setResendingEmail(true);
    try {
      const result = await contractsService.resendContractWelcomeEmail(code);
      toast.success(result.sent_to ? `Correo de bienvenida reenviado a ${result.sent_to}` : result.message || 'Correo de bienvenida reenviado');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | string[] }; status?: number } };
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0] : typeof detail === 'string' ? detail : null;
      if (status === 404 || status === 400) {
        toast.error(message || 'No se pudo reenviar el correo.');
      } else {
        toast.error(message || 'Error al enviar el correo. Revise logs o configuración.');
      }
    } finally {
      setResendingEmail(false);
    }
  };

  const handleExpire = async () => {
    if (!contract || !code) return;
    setExpiring(true);
    try {
      await contractsService.expireContract(code);
      await loadContract();
      setShowExpireModal(false);
    } catch (err: unknown) {
      console.error('Error expirando contrato:', err);
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : e?.message || 'Error al expirar el contrato');
    } finally {
      setExpiring(false);
    }
  };

  const handleDelete = async () => {
    if (!contract || !code) return;
    setDeleting(true);
    try {
      await contractsService.deleteContract(code);
      setShowDeleteModal(false);
      navigate(contractsListPath);
    } catch (err: unknown) {
      console.error('Error eliminando contrato:', err);
      const e = err as { response?: { data?: { detail?: unknown }; status?: number }; message?: string };
      if (e?.response?.status === 400) {
        toast.error('No se puede eliminar un contrato ya pagado.');
      } else {
        toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : e?.message || 'Error al eliminar el contrato');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenUpdateStatusModal = () => {
    if (!contract) return;
    
    // Para grado C, solo permitir suscripción (no existe pago único)
    const defaultPaymentType = contract.grade === 'C'
      ? 'subscription'
      : (contract.payment_type || 'one_time');
    const paymentType = contract.grade === 'C' && (contract.payment_type === 'one_time' || contract.payment_type === 'one_time_full')
      ? 'subscription'
      : defaultPaymentType;
    const contractType = getContractTypeFromRaw(contract.contract_type);
    const isOneTimeFull = paymentType === 'one_time_full';
    const expectedAmount = isOneTimeFull
      ? contract.amount ?? 0
      : getExpectedAmountByPaymentType(paymentType as 'one_time' | 'subscription', contract.grade, contractType);

    setUpdateForm({
      status: contract.status,
      amount: isOneTimeFull ? String(contract.amount ?? '') : expectedAmount.toFixed(2),
      payment_type: paymentType as 'one_time' | 'subscription' | 'one_time_full',
      manual_payment_confirmed: contract.manual_payment_confirmed || false,
      manual_payment_method: contract.manual_payment_method || '',
      manual_payment_note: contract.manual_payment_note || '',
      subscription_id: contract.subscription_id || '',
      subscription_status: contract.subscription_status || '',
      first_payment_amount: contract.first_payment_amount ? Number(contract.first_payment_amount).toFixed(2) : '',
    });
    setShowUpdateStatusModal(true);
  };

  const handleCloseUpdateStatusModal = () => {
    setShowUpdateStatusModal(false);
    setUpdateForm({
      status: 'pending',
      amount: '',
      payment_type: 'one_time',
      manual_payment_confirmed: false,
      manual_payment_method: '',
      manual_payment_note: '',
      subscription_id: '',
      subscription_status: '',
      first_payment_amount: '',
    });
  };

  const handleUpdateStatus = async () => {
    if (!contract || !code) return;

    const contractType = getContractTypeFromRaw(contract.contract_type);
    const parsedAmount = parseContractAmountInput(updateForm.amount);
    const isOneTimeFull = updateForm.payment_type === 'one_time_full';
    const expectedAmount = isOneTimeFull ? null : getExpectedAmountByPaymentType(updateForm.payment_type, contract.grade, contractType);

    if (parsedAmount === null || parsedAmount <= 0) {
      toast.error('Introduce un importe válido mayor que 0.');
      return;
    }
    if (!isOneTimeFull && expectedAmount != null && !isAmountMatchingExpected(parsedAmount, expectedAmount)) {
      toast.error(`El importe no coincide con el tipo de pago seleccionado. Debe ser ${expectedAmount.toFixed(2)} €.`);
      return;
    }
    
    setUpdating(true);
    try {
      const updateData: ContractUpdateRequest = {
        status: updateForm.status,
      };

      // El importe ya fue validado contra el tipo de pago.
      updateData.amount = parsedAmount;

      // Tipo de pago
      if (updateForm.payment_type) {
        updateData.payment_type = updateForm.payment_type;
      }

      // Pago parcial (primer pago)
      if (updateForm.first_payment_amount && parseFloat(updateForm.first_payment_amount) > 0) {
        updateData.first_payment_amount = parseFloat(updateForm.first_payment_amount);
      }

      // Suscripción
      if (updateForm.subscription_id) {
        updateData.subscription_id = updateForm.subscription_id;
      }
      if (updateForm.subscription_status) {
        updateData.subscription_status = updateForm.subscription_status;
      }

      // Si el estado es 'paid' o 'completed', y se marca como pago manual
      if ((updateForm.status === 'paid' || updateForm.status === 'completed') && updateForm.manual_payment_confirmed) {
        updateData.manual_payment_confirmed = true;
        if (updateForm.manual_payment_method) {
          updateData.manual_payment_method = updateForm.manual_payment_method;
        }
        if (updateForm.manual_payment_note) {
          updateData.manual_payment_note = updateForm.manual_payment_note;
        }
      }

      const updatedContract = await contractsService.updateContract(code, updateData);
      setContract(updatedContract);
      setShowUpdateStatusModal(false);
      toast.success('Estado del contrato actualizado correctamente');
    } catch (error: unknown) {
      console.error('Error actualizando contrato:', error);
      const err = error as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : undefined) ||
          (typeof err?.message === 'string' ? err.message : undefined) ||
          'Error al actualizar el estado del contrato'
      );
    } finally {
      setUpdating(false);
    }
  };

  // Actualizar título de la página con el código del contrato
  const contractTitle = contract?.hiring_code 
    ? `Contrato ${contract.hiring_code} - Detalle de Contrato | Migro.es`
    : 'Detalle de Contrato | Migro.es';
  usePageTitle(contract ? contractTitle : undefined);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" text="Cargando contrato..." />
      </div>
    );
  }

  if (!contract) {
    return (
      <EmptyState
        icon={<DocumentTextIcon width={48} height={48} className="text-gray-400" />}
        title="Contrato no encontrado"
        description="El contrato que buscas no existe o ha sido eliminado."
        action={
          <Button onClick={goBack}>
            Volver a Contratos
          </Button>
        }
      />
    );
  }

  const getStatusBadge = (status: Contract['status']) => {
    const colors = CONTRACT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    const labels: Record<Contract['status'], string> = {
      pending: 'Pendiente',
      paid: 'Pagado',
      completed: 'Completado',
      expired: 'Expirado',
      cancelled: 'Cancelado',
    };
    return (
      <Badge className={colors}>
        {labels[status]}
      </Badge>
    );
  };

  const getGradeBadge = (grade?: Contract['grade']) => {
    if (!grade) return null;
    const colors = GRADE_COLORS[grade] || 'bg-gray-100 text-gray-800';
    return (
      <Badge className={colors}>
        Grado {grade}
      </Badge>
    );
  };

  const isExpired = contract ? !!contract.expires_at && new Date(contract.expires_at) < new Date() : false;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header compacto mobile-first: siempre visible */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Button onClick={goBack} variant="outline" size="sm" className="flex-shrink-0">
            <ArrowLeftIcon width={16} height={16} className="mr-2" />
            Volver
          </Button>
          <span className="font-mono text-sm text-gray-500">{contract.hiring_code}</span>
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{contract.client_name}</h1>
          <p className="text-sm text-gray-600">{contract.client_email}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {getStatusBadge(contract.status)}
            {getGradeBadge(contract.grade)}
            {contract.is_custom && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Personalizado
              </span>
            )}
            <span
              className="text-sm text-gray-600"
              title={getPaymentTypeTitle(contract.payment_type)}
            >
              {contract.service_name || 'Servicio'} · {contract.is_custom && contract.num_installments
                ? `${contract.num_installments} cuota${contract.num_installments > 1 ? 's' : ''}`
                : getPaymentTypeLabel(contract.payment_type)}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(contract.amount, contract.currency, false)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowEditModal(true)} variant="default" size="sm" className="bg-green-600 hover:bg-green-700 text-white">
            <PencilIcon width={16} height={16} className="mr-2" />
            Editar
          </Button>
          <Button onClick={handleCopyLink} variant="outline" size="sm">
            {copied ? <CheckIcon width={16} height={16} className="mr-2" /> : <DocumentDuplicateIcon width={16} height={16} className="mr-2" />}
            {copied ? 'Copiado' : 'Copiar Link'}
          </Button>
          <Button onClick={handleOpenLink} variant="outline" size="sm">
            <ArrowTopRightOnSquareIcon width={16} height={16} className="mr-2" />
            Abrir
          </Button>
          <Button onClick={handleOpenMandateLink} variant="outline" size="sm" title="Abrir página de firma del mandato">
            <ArrowTopRightOnSquareIcon width={16} height={16} className="mr-2" />
            Ir a mandato
          </Button>
          <Button onClick={handleResendWelcomeEmail} disabled={resendingEmail} variant="outline" size="sm" title="Reenviar correo de bienvenida con enlace a contratación y PDF">
            <EnvelopeIcon width={16} height={16} className="mr-2" />
            {resendingEmail ? 'Enviando...' : 'Reenviar correo de bienvenida'}
          </Button>
          {contract.mandate_status === 'signed' && (
            <Button onClick={handleDownloadMandate} variant="outline" size="sm" title="Ver o descargar PDF del mandato firmado">
              <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
              Descargar mandato
            </Button>
          )}
          <Button onClick={() => handleDownload(false)} disabled={downloading} variant="outline" size="sm">
            <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
            {downloading ? 'Descargando...' : 'Descargar'}
          </Button>
          {(contract.status === 'paid' || contract.status === 'completed' || contract.final_contract_url) && (
            <Button
              onClick={handleDownloadFinalSigned}
              disabled={downloading}
              variant="outline"
              size="sm"
              title={!contract.final_contract_url ? 'Contrato definitivo no disponible (se genera tras el pago confirmado)' : undefined}
            >
              <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
              {downloading ? 'Descargando...' : 'Contrato definitivo'}
            </Button>
          )}
          {contract.status !== 'paid' && contract.status !== 'completed' && !isExpired && (
            <Button onClick={() => setShowExpireModal(true)} variant="outline" size="sm" className="text-amber-700 border-amber-300">
              Expirar código
            </Button>
          )}
          {contract.status !== 'paid' && contract.status !== 'completed' && (
            <Button onClick={() => setShowDeleteModal(true)} variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50">
              <TrashIcon width={16} height={16} className="mr-2" />
              Eliminar
            </Button>
          )}
        </div>
      </div>

      {/* Tabs - Mobile first: scroll horizontal */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex gap-0 min-w-max sm:min-w-0 sm:flex-wrap" aria-label="Secciones del contrato">
          {[
            { id: 'general' as const, label: 'Info', icon: DocumentTextIcon },
            { id: 'cliente' as const, label: 'Cliente', icon: UserIcon },
            { id: 'firmas' as const, label: 'Firmas', icon: PencilSquareIcon },
            { id: 'pagos' as const, label: 'Pagos', icon: BanknotesIcon },
            { id: 'stripe' as const, label: 'Stripe', icon: CreditCardIcon },
            { id: 'anexos' as const, label: 'Anexos', icon: DocumentIcon },
            { id: 'mandato' as const, label: 'Mandato', icon: ScaleIcon },
            { id: 'actividad' as const, label: 'Actividad', icon: ChartBarIcon },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`contract-tab-${id}`}
              onClick={() => setActiveTab(id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap
                transition-colors touch-manipulation
                ${activeTab === id
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content by Tab */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {activeTab === 'cliente' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon width={20} height={20} />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {contract.contact_id && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Vinculado con Contacto del CRM</span>
                    </div>
                    <Button
                      onClick={() => navigate(`/crm/contacts/${contract.contact_id}`)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <LinkIcon width={14} height={14} />
                      Ver Contacto
                    </Button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Nombre</label>
                  <div className="text-sm text-gray-900 mt-1">{contract.client_name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <div className="text-sm text-gray-900 mt-1 flex items-center gap-2">
                    {contract.client_email}
                    <a
                      href={`mailto:${contract.client_email}`}
                      className="text-green-600 hover:text-green-700"
                    >
                      <EnvelopeIcon width={16} height={16} />
                    </a>
                  </div>
                </div>
                {contract.client_passport && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Pasaporte</label>
                    <div className="text-sm text-gray-900 mt-1">{contract.client_passport}</div>
                  </div>
                )}
                {contract.client_nie && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">NIE</label>
                    <div className="text-sm text-gray-900 mt-1">{contract.client_nie}</div>
                  </div>
                )}
                {contract.client_address && (
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Dirección</label>
                    <div className="text-sm text-gray-900 mt-1">
                      {contract.client_address}
                      {contract.client_city && `, ${contract.client_city}`}
                      {contract.client_province && `, ${contract.client_province}`}
                      {contract.client_postal_code && ` ${contract.client_postal_code}`}
                    </div>
                  </div>
                )}
                {contract.client_nationality && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nacionalidad</label>
                    <div className="text-sm text-gray-900 mt-1">{contract.client_nationality}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {activeTab === 'general' && (
          <>
          {/* Service Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DocumentTextIcon width={20} height={20} />
                Información del Servicio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Nombre del Servicio</label>
                <div className="text-base font-semibold text-gray-900 mt-1">
                  {contract.service_name || 'No especificado'}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Descripción del Servicio</label>
                <div className="text-sm text-gray-700 mt-1 bg-gray-50 p-3 rounded-lg">
                  {contract.service_description || 'No hay descripción disponible'}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {getGradeBadge(contract.grade)}
                {contract.payment_type && (
                  <Badge variant="outline" title={getPaymentTypeTitle(contract.payment_type)}>
                    {getPaymentTypeLabel(contract.payment_type).replace(/^\w/, (c) => c.toUpperCase())}
                  </Badge>
                )}
                {contract.is_custom && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    Personalizado
                  </Badge>
                )}
                {contract.language && contract.language !== 'es' && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {contract.language === 'fr' ? '🇫🇷 Français' : contract.language === 'en' ? '🇬🇧 English' : '🇪🇸 Español'}
                  </Badge>
                )}
              </div>
              {/* Info adicional contrato personalizado */}
              {contract.is_custom && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium text-purple-900">Contrato personalizado</p>
                  {contract.num_installments && (
                    <p className="text-sm text-purple-700">
                      Cuotas: <strong>{contract.num_installments}</strong>
                      {contract.num_installments > 1 && contract.amount && (
                        <span> ({(contract.amount / contract.num_installments).toFixed(2)}€ c/u)</span>
                      )}
                    </p>
                  )}
                  {contract.custom_payment_method && (
                    <p className="text-sm text-purple-700">
                      Método: <strong>{contract.custom_payment_method}</strong>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contrato vinculado (2x1) */}
          {contract.contract_type === 'linked' && (contract.linked_contract_code || contract.linked_partner_name) && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="text-base">Contrato vinculado (2x1)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {contract.linked_partner_name && (
                  <p className="text-sm text-gray-700">Segundo titular: <strong>{contract.linked_partner_name}</strong></p>
                )}
                {contract.linked_contract_code && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`${contractsListPath}/${contract.linked_contract_code}`)}
                    className="gap-2"
                  >
                    <LinkIcon width={14} height={14} />
                    Ver contrato {contract.linked_contract_code}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Information - solo en General */}
          {(contract.payment_intent_id || contract.manual_payment_confirmed) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CurrencyDollarIcon width={20} height={20} />
                  Información de Pago
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contract.payment_intent_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Intent ID</label>
                    <div className="text-sm text-gray-900 mt-1 font-mono break-all">{contract.payment_intent_id}</div>
                    <a
                      href={`https://dashboard.stripe.com/payments/${contract.payment_intent_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-600 hover:text-green-700 mt-1 inline-flex items-center gap-1"
                    >
                      Ver en Stripe <ArrowTopRightOnSquareIcon width={14} height={14} />
                    </a>
                  </div>
                )}
                {contract.subscription_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Subscription ID</label>
                    <div className="text-sm text-gray-900 mt-1 font-mono break-all">{contract.subscription_id}</div>
                    {contract.subscription_status && (
                      <div className="text-sm text-gray-600 mt-1">
                        Estado: <Badge variant="outline">{contract.subscription_status}</Badge>
                      </div>
                    )}
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${contract.subscription_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-600 hover:text-green-700 mt-1 inline-flex items-center gap-1"
                    >
                      Ver en Stripe <ArrowTopRightOnSquareIcon width={14} height={14} />
                    </a>
                  </div>
                )}
                {contract.manual_payment_confirmed && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Pago Manual</label>
                    <div className="text-sm text-gray-900 mt-1">
                      Confirmado
                      {contract.manual_payment_method && ` - ${contract.manual_payment_method}`}
                    </div>
                    {contract.manual_payment_note && (
                      <div className="text-sm text-gray-600 mt-1">{contract.manual_payment_note}</div>
                    )}
                  </div>
                )}
                {(contract.first_payment_amount || contract.payment_type === 'one_time_full') && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      {contract.payment_type === 'one_time_full' ? 'Pago único' : 'Primer Pago'}
                    </label>
                    <div className="text-sm text-gray-900 mt-1">
                      {formatCurrency(
                        contract.payment_type === 'one_time_full' ? contract.amount : (contract.first_payment_amount ?? 0),
                        contract.currency,
                        false
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contract History - solo en General */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClockIcon width={20} height={20} />
                Historial del Contrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Timeline */}
                <div className="relative">
                  {/* Created / Draft */}
                  <div className="flex gap-4 pb-6">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <DocumentTextIcon className="text-blue-600" width={20} height={20} />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" />
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">Borrador Creado</p>
                          <p className="text-sm text-gray-500 mt-1">{formatDateOnly(contract.created_at)}</p>
                        </div>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          Borrador
                        </Badge>
                      </div>
                      {contract.contract_pdf_url && (
                        <div className="mt-2">
                          <a
                            href={contract.contract_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-green-600 hover:text-green-700 inline-flex items-center gap-1"
                          >
                            <DocumentIcon width={14} height={14} />
                            Ver PDF del borrador
                            <ArrowTopRightOnSquareIcon width={12} height={12} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signed / Contract Accepted */}
                  {contract.contract_accepted_at && (
                    <div className="flex gap-4 pb-6">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircleIcon className="text-green-600" width={20} height={20} />
                        </div>
                        {(contract.payment_intent_id || contract.subscription_id || contract.manual_payment_confirmed || stripeSummary?.subscription) && (
                          <div className="w-0.5 h-full bg-gray-200 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">Contrato Firmado</p>
                            <p className="text-sm text-gray-500 mt-1">{formatDateOnly(contract.contract_accepted_at)}</p>
                          </div>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Firmado
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment: mostrar si hay pago en contrato O suscripción/facturas en Stripe */}
                  {(contract.payment_intent_id || contract.subscription_id || contract.manual_payment_confirmed || stripeSummary?.subscription) && (
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <CreditCardIcon className="text-purple-600" width={20} height={20} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {contract.manual_payment_confirmed ? 'Pago Manual Confirmado' : 'Pago Procesado'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {contract.updated_at
                                ? formatDateOnly(contract.updated_at)
                                : stripeSummary?.transactions?.[0]?.created
                                  ? formatDateOnly(
                                      typeof stripeSummary.transactions[0].created === 'number'
                                        ? new Date(stripeSummary.transactions[0].created * 1000)
                                        : stripeSummary.transactions[0].created
                                    )
                                  : stripeSummary?.invoices?.[0]?.created
                                    ? formatDateOnly(
                                        typeof stripeSummary.invoices[0].created === 'number'
                                          ? new Date(stripeSummary.invoices[0].created * 1000)
                                          : stripeSummary.invoices[0].created
                                      )
                                    : stripeSummary?.subscription?.current_period_start
                                      ? formatDateOnly(
                                          typeof stripeSummary.subscription.current_period_start === 'number'
                                            ? new Date(stripeSummary.subscription.current_period_start * 1000)
                                            : stripeSummary.subscription.current_period_start
                                        )
                                      : 'Fecha no disponible'}
                            </p>
                          </div>
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            Pagado
                          </Badge>
                        </div>
                        <div className="mt-3 space-y-2">
                          {contract.payment_intent_id && (
                            <div className="text-sm bg-gray-50 p-2 rounded border">
                              <p className="font-medium text-gray-700">Stripe Payment Intent:</p>
                              <p className="text-gray-600 font-mono text-xs break-all mt-1">
                                {contract.payment_intent_id}
                              </p>
                              <a
                                href={`https://dashboard.stripe.com/payments/${contract.payment_intent_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-green-600 hover:text-green-700 mt-1 inline-flex items-center gap-1"
                              >
                                Ver en Stripe <ArrowTopRightOnSquareIcon width={12} height={12} />
                              </a>
                            </div>
                          )}
                          {(contract.subscription_id || stripeSummary?.subscription?.id) && (
                            <div className="text-sm bg-gray-50 p-2 rounded border">
                              <p className="font-medium text-gray-700">Stripe Subscription:</p>
                              <p className="text-gray-600 font-mono text-xs break-all mt-1">
                                {contract.subscription_id || stripeSummary?.subscription?.id}
                              </p>
                              {(contract.subscription_status || stripeSummary?.subscription?.status) && (
                                <p className="text-gray-600 text-xs mt-1">
                                  Estado: <span className="font-semibold">{contract.subscription_status || stripeSummary?.subscription?.status}</span>
                                </p>
                              )}
                              <a
                                href={`https://dashboard.stripe.com/subscriptions/${contract.subscription_id || stripeSummary?.subscription?.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-green-600 hover:text-green-700 mt-1 inline-flex items-center gap-1"
                              >
                                Ver en Stripe <ArrowTopRightOnSquareIcon width={12} height={12} />
                              </a>
                            </div>
                          )}
                          {contract.manual_payment_confirmed && (
                            <div className="text-sm bg-gray-50 p-2 rounded border">
                              <p className="font-medium text-gray-700">Pago Manual:</p>
                              <p className="text-gray-600 mt-1">
                                {contract.manual_payment_method || 'Método no especificado'}
                              </p>
                              {contract.manual_payment_note && (
                                <p className="text-gray-600 text-xs mt-1 italic">{contract.manual_payment_note}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          </>
          )}

          {activeTab === 'firmas' && (
            <ContractSignaturesTab
              hiringCode={contract.hiring_code}
              clientName={contract.client_name}
              contractType={contract.contract_type}
            />
          )}

          {activeTab === 'pagos' && (
            <ContractPaymentsTab
              contract={contract}
              stripeSummary={stripeSummary}
              onContractUpdate={loadContract}
            />
          )}

          {activeTab === 'anexos' && (
            <ContractAnnexes hiringCode={contract.hiring_code} />
          )}

          {activeTab === 'stripe' && contract?.hiring_code && (
            <StripeBillingSection hiringCode={contract.hiring_code} />
          )}

          {activeTab === 'mandato' && (
            <ContractMandateTab
              contract={contract}
              onContractUpdate={loadContract}
              onNavigateToContract={(code) => navigate(`${contractsListPath}/${code}`)}
            />
          )}

          {activeTab === 'actividad' && code && (
            <ActivityLogBox entityType="contract" entityId={code} limit={50} />
          )}
        </div>

        {/* Right Column - Metadata (visible en General) */}
        {activeTab === 'general' && (
        <div className="space-y-4 sm:space-y-6">
          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon width={20} height={20} />
                Fechas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Creado</label>
                <div className="text-sm text-gray-900 mt-1">{formatDate(contract.created_at)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Actualizado</label>
                <div className="text-sm text-gray-900 mt-1">{formatDate(contract.updated_at)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Expira</label>
                <div className={`text-sm mt-1 ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                  {formatDate(contract.expires_at)}
                  {isExpired && ' (Expirado)'}
                </div>
              </div>
              {contract.contract_accepted_at && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Contrato Aceptado</label>
                  <div className="text-sm text-gray-900 mt-1">{formatDate(contract.contract_accepted_at)}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expediente Link */}
          <ContractExpedienteLink
            hiringCode={contract.hiring_code}
            expedienteId={contract.expediente_id}
            onLinked={loadContract}
          />

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => {
                  console.log('🖊️ Abriendo modal de edición completa');
                  setShowEditModal(true);
                }}
                variant="default"
                className="w-full justify-start bg-green-600 hover:bg-green-700 text-white border-2 border-green-700"
                size="sm"
              >
                <PencilIcon width={16} height={16} className="mr-2" />
                Editar Contrato Completo
              </Button>
              <Button
                onClick={handleOpenUpdateStatusModal}
                variant="default"
                className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <CurrencyDollarIcon width={16} height={16} className="mr-2" />
                Modificar Estado y Pago
              </Button>
              <Button
                onClick={handleOpenLink}
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                <ArrowTopRightOnSquareIcon width={16} height={16} className="mr-2" />
                Ver en Frontend
              </Button>
              <Button
                onClick={handleOpenMandateLink}
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                <ArrowTopRightOnSquareIcon width={16} height={16} className="mr-2" />
                Ir a mandato
              </Button>
              <Button
                onClick={handleResendWelcomeEmail}
                disabled={resendingEmail}
                variant="outline"
                className="w-full justify-start"
                size="sm"
                title="Reenviar correo de bienvenida al cliente (enlace a terminar contratación + PDF)"
              >
                <EnvelopeIcon width={16} height={16} className="mr-2" />
                {resendingEmail ? 'Enviando...' : 'Reenviar correo de bienvenida'}
              </Button>
              {contract.mandate_status === 'signed' && (
                <Button
                  onClick={handleDownloadMandate}
                  variant="outline"
                  className="w-full justify-start"
                  size="sm"
                >
                  <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
                  Descargar mandato
                </Button>
              )}
              <Button
                onClick={() => handleDownload(false)}
                disabled={downloading}
                variant="outline"
                className="w-full justify-start"
                size="sm"
              >
                <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
                Descargar PDF
              </Button>
              {(contract.status === 'paid' || contract.status === 'completed' || contract.final_contract_url) && (
                <Button
                  onClick={handleDownloadFinalSigned}
                  disabled={downloading}
                  variant="outline"
                  className="w-full justify-start"
                  size="sm"
                  title={!contract.final_contract_url ? 'Contrato definitivo no disponible (se genera tras el pago confirmado)' : undefined}
                >
                  <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
                  {downloading ? 'Descargando...' : 'Descargar contrato definitivo firmado'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {contract.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{contract.notes}</div>
              </CardContent>
            </Card>
          )}
        </div>
        )}
      </div>

      {/* Modal para Modificar Estado y Pago */}
      <Modal
        open={showUpdateStatusModal}
        onClose={handleCloseUpdateStatusModal}
        title="Modificar Estado y Pago"
        size="md"
        footer={
          <>
            <Button
              onClick={handleCloseUpdateStatusModal}
              variant="outline"
              disabled={updating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateStatus}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={
                updating ||
                !contract ||
                (updateForm.payment_type !== 'one_time_full' &&
                  !isAmountMatchingExpected(
                    parseContractAmountInput(updateForm.amount),
                    getExpectedAmountByPaymentType(
                      updateForm.payment_type,
                      contract.grade,
                      getContractTypeFromRaw(contract.contract_type)
                    )
                  ))
              }
            >
              {updating ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
              <div>
                <Label htmlFor="status">Estado del Contrato *</Label>
                <select
                  id="status"
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value as ContractStatus })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagado</option>
                  <option value="completed">Completado</option>
                  <option value="expired">Expirado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              <div>
                <Label htmlFor="payment_type">Tipo de Pago *</Label>
                <select
                  id="payment_type"
                  value={updateForm.payment_type}
                  onChange={(e) => {
                    const newPaymentType = e.target.value as 'one_time' | 'subscription' | 'one_time_full';
                    if (contract?.grade === 'C') {
                      const expectedAmount = getExpectedAmountByPaymentType(
                        'subscription',
                        contract.grade,
                        getContractTypeFromRaw(contract.contract_type)
                      );
                      setUpdateForm(prev => ({ ...prev, payment_type: 'subscription', amount: expectedAmount.toFixed(2) }));
                    } else if (newPaymentType === 'one_time_full') {
                      setUpdateForm({ ...updateForm, payment_type: 'one_time_full', amount: updateForm.amount || String(contract?.amount ?? '') });
                    } else {
                      const expectedAmount = getExpectedAmountByPaymentType(
                        newPaymentType,
                        contract?.grade,
                        getContractTypeFromRaw(contract?.contract_type)
                      );
                      setUpdateForm({ ...updateForm, payment_type: newPaymentType, amount: expectedAmount.toFixed(2) });
                    }
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {contract?.grade !== 'C' && (
                    <option value="one_time">{getPricingMessages('one_time').option}</option>
                  )}
                  {contract?.grade !== 'C' && (
                    <option value="one_time_full">{getPricingMessages('one_time_full').option}</option>
                  )}
                  <option value="subscription">
                    {getPricingMessages('subscription').option}
                  </option>
                </select>
                {contract?.grade === 'C' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Para expedientes grado C solo está disponible la opción de pago mensual ({getPricingMessages('subscription').option})
                  </p>
                )}
                {contract?.grade !== 'C' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {getPricingMessages('one_time').hint} | {getPricingMessages('subscription').hint}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="amount">Importe Total (€) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={updateForm.amount}
                  onChange={(e) => setUpdateForm({ ...updateForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
                {contract && (
                  <>
                    <p className="text-xs text-gray-500 mt-1">
                      Importe actual: {formatCurrency(contract.amount, contract.currency, false)}
                    </p>
                    {updateForm.payment_type !== 'one_time_full' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Importe esperado para este tipo de pago:{' '}
                        {getExpectedAmountByPaymentType(
                          updateForm.payment_type,
                          contract.grade,
                          getContractTypeFromRaw(contract.contract_type)
                        ).toFixed(2)} €
                      </p>
                    )}
                    {updateForm.payment_type === 'one_time' && (
                      <p className="text-xs text-blue-600 mt-1">
                        {getPricingMessages('one_time').recommendation}
                      </p>
                    )}
                    {updateForm.payment_type === 'one_time_full' && (
                      <p className="text-xs text-blue-600 mt-1">
                        {getPricingMessages('one_time_full').recommendation}
                      </p>
                    )}
                    {updateForm.payment_type === 'subscription' && (
                      <p className="text-xs text-blue-600 mt-1">
                        {getPricingMessages('subscription').recommendation}
                      </p>
                    )}
                    {updateForm.payment_type !== 'one_time_full' &&
                      !isAmountMatchingExpected(
                        parseContractAmountInput(updateForm.amount),
                        getExpectedAmountByPaymentType(
                          updateForm.payment_type,
                          contract.grade,
                          getContractTypeFromRaw(contract.contract_type)
                        )
                      ) && (
                        <p className="text-xs text-red-600 mt-1">
                          El importe no cuadra con el tipo de pago seleccionado. Corrígelo para poder guardar.
                        </p>
                      )}
                  </>
                )}
              </div>

              {updateForm.payment_type === 'subscription' && (
                <>
                  <div>
                    <Label htmlFor="first_payment_amount">Primer Pago / Pago Parcial (€)</Label>
                    <Input
                      id="first_payment_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={updateForm.first_payment_amount}
                      onChange={(e) => setUpdateForm({ ...updateForm, first_payment_amount: e.target.value })}
                      placeholder="0.00"
                    />
                    {contract?.first_payment_amount && (
                      <p className="text-xs text-gray-500 mt-1">
                        Primer pago actual: {formatCurrency(contract.first_payment_amount, contract.currency, false)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Monto del primer pago en suscripciones (normalmente 10% del total)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="subscription_id">Subscription ID (Stripe)</Label>
                    <Input
                      id="subscription_id"
                      value={updateForm.subscription_id}
                      onChange={(e) => setUpdateForm({ ...updateForm, subscription_id: e.target.value })}
                      placeholder="sub_xxxxxxxxxxxxx"
                    />
                    {contract?.subscription_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        ID actual: {contract.subscription_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="subscription_status">Estado de Suscripción</Label>
                    <select
                      id="subscription_status"
                      value={updateForm.subscription_status}
                      onChange={(e) => setUpdateForm({ ...updateForm, subscription_status: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Seleccionar estado...</option>
                      <option value="active">Activa</option>
                      <option value="canceled">Cancelada</option>
                      <option value="past_due">Pago Vencido</option>
                      <option value="unpaid">No Pagada</option>
                      <option value="incomplete">Incompleta</option>
                      <option value="trialing">En Prueba</option>
                    </select>
                    {contract?.subscription_status && (
                      <p className="text-xs text-gray-500 mt-1">
                        Estado actual: {contract.subscription_status}
                      </p>
                    )}
                  </div>
                </>
              )}

              {(updateForm.status === 'paid' || updateForm.status === 'completed') && (
                <>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="manual_payment_confirmed"
                      checked={updateForm.manual_payment_confirmed}
                      onChange={(e) => setUpdateForm({ ...updateForm, manual_payment_confirmed: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <Label htmlFor="manual_payment_confirmed" className="cursor-pointer">
                      Pago realizado externamente
                    </Label>
                  </div>

                  {updateForm.manual_payment_confirmed && (
                    <>
                      <div>
                        <Label htmlFor="manual_payment_method">Método de Pago</Label>
                        <Input
                          id="manual_payment_method"
                          value={updateForm.manual_payment_method}
                          onChange={(e) => setUpdateForm({ ...updateForm, manual_payment_method: e.target.value })}
                          placeholder="Ej: Transferencia bancaria, Efectivo, etc."
                        />
                      </div>

                      <div>
                        <Label htmlFor="manual_payment_note">Nota sobre el Pago</Label>
                        <Textarea
                          id="manual_payment_note"
                          value={updateForm.manual_payment_note}
                          onChange={(e) => setUpdateForm({ ...updateForm, manual_payment_note: e.target.value })}
                          placeholder="Ej: Transferencia bancaria del 24/11/2025 - Referencia 123456"
                          rows={3}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
        </div>
      </Modal>

      {/* Modal de Edición Completa */}
      {contract && (
        <EditContractModal
          contract={contract}
          visible={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updatedContract) => {
            setContract(updatedContract);
            setShowEditModal(false);
            loadContract();
          }}
        />
      )}

      {/* Modal confirmar expirar */}
      <Modal
        open={showExpireModal}
        onClose={() => !expiring && setShowExpireModal(false)}
        title="Expirar código de contratación"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowExpireModal(false)} disabled={expiring}>
              Cancelar
            </Button>
            <Button onClick={handleExpire} disabled={expiring} className="bg-amber-600 hover:bg-amber-700 text-white">
              {expiring ? 'Expirando...' : 'Expirar código'}
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          ¿Estás seguro de que deseas expirar el código <strong>{contract?.hiring_code}</strong>? El cliente ya no podrá usarlo para acceder al flujo de contratación.
        </p>
      </Modal>

      {/* Modal confirmar eliminar */}
      <Modal
        open={showDeleteModal}
        onClose={() => !deleting && setShowDeleteModal(false)}
        title="Eliminar contrato"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button onClick={handleDelete} disabled={deleting} variant="destructive">
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          ¿Estás seguro de que deseas eliminar el contrato <strong>{contract?.hiring_code}</strong>? Esta acción no se puede deshacer. No podrás eliminar un contrato ya pagado.
        </p>
      </Modal>
    </div>
  );
}



